import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Newspaper } from "lucide-react";
import { ARTICLES } from "@/articles";
import { useSeo } from "@/hooks/use-seo";
import { Navbar, Footer } from "@/pages/Home";

export default function Blog() {
  useSeo({
    title: "Cabin Crew Insider Guides & Articles | Cabin Crew Mentoring",
    description: "Practical cabin crew career advice from Tony's years in the cabin and on an airline hiring team — assessment days, group exercises, interviews and ground school.",
    path: "/blog",
  });
  return (
    <div data-testid="blog-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-32 pb-24 max-w-5xl mx-auto px-4 sm:px-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-sky-700">
          <Newspaper className="w-4 h-4" /> From the galley
        </div>
        <h1 data-testid="blog-heading" className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mt-2">
          Insider guides &amp; articles
        </h1>
        <p className="text-slate-500 mt-3 max-w-2xl text-sm leading-relaxed">
          Practical advice from Tony's years in the cabin and on the hiring team — written for people chasing their wings.
        </p>

        <div className="mt-12 grid gap-6">
          {ARTICLES.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Link
                to={`/blog/${a.slug}`}
                data-testid={`blog-card-${a.slug}`}
                className="block rounded-xl bg-white border border-slate-200 p-8 hover:-translate-y-1 hover:shadow-lg hover:border-sky-300 transition-[transform,box-shadow,border-color] duration-300 group"
              >
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="rounded-full bg-sky-100 text-sky-800 font-bold px-3 py-1 uppercase tracking-wider">{a.category}</span>
                  <span className="text-slate-400">{a.date}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-400">{a.readTime}</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#0A192F] mt-4 group-hover:text-sky-800 transition-colors duration-200">
                  {a.title}
                </h2>
                <p className="text-slate-500 text-sm leading-relaxed mt-3 max-w-3xl">{a.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-sky-700 group-hover:gap-2.5 transition-[gap] duration-200">
                  Read article <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 navy-panel rounded-2xl p-10 text-center text-white">
          <h2 className="font-display uppercase text-3xl font-bold">Reading is a start.<br /><span className="text-[#F59E0B]">Practice gets you hired.</span></h2>
          <Link
            to="/book"
            data-testid="blog-cta-btn"
            className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#F59E0B] text-[#0A192F] font-bold px-8 py-3.5 hover:bg-amber-400 transition-colors duration-200"
          >
            Book a Session with Tony <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
